/**
 * KENO — 20 distinct numbers drawn from 1–80 each round, with every bet read off them.
 *
 * Pure functions only, shared by the worker (which settles), the API (which validates)
 * and the KENO client (which displays): one definition of "what did this draw mean", so
 * the figure on a member's screen and the figure that paid them cannot disagree.
 *
 * Rules are the reference game's, as published on its rules page (docs/keno-reference/
 * SPEC.md). Worth stating the two that differ from intuition:
 *
 *  - มังกร / เสือ on a DT tie is a PUSH: the stake comes back, the bet neither wins nor
 *    loses. Only "DT-เท่ากัน" wins.
 *  - บน / ล่าง on a 10:10 tie simply LOSES — no refund. Only "UD-เท่ากัน" wins.
 */
export const KENO_BALLS = 80;
export const KENO_DRAWN = 20;
/** Every bet the board offers, in the order of the reference's odds keys o1…o21. */
export const KENO_BET_TYPES = [
    'big', 'small',
    'odd', 'even',
    'dragon', 'dt_tie', 'tiger',
    'up', 'ud_tie', 'down',
    'big_odd', 'big_even', 'small_odd', 'small_even',
    'gold', 'wood', 'water', 'fire', 'earth',
];
export function isKenoBetType(v) {
    return typeof v === 'string' && KENO_BET_TYPES.includes(v);
}
/**
 * The other side of a two-sided bet. A member may not hold both sides of one pair in the
 * same round (the reference game's "Cannot bet on both side."): one of them is a certain
 * loss, so the pair only churns turnover. Ties and the five elements have no opposite.
 */
export const KENO_OPPOSITE = {
    big: 'small', small: 'big',
    odd: 'even', even: 'odd',
    dragon: 'tiger', tiger: 'dragon',
    up: 'down', down: 'up',
    big_odd: 'big_even', big_even: 'big_odd',
    small_odd: 'small_even', small_even: 'small_odd',
};
/** The reference's published rates. Stored per table in games.config, editable later. */
export const KENO_DEFAULT_PAYOUTS = {
    big: 1.95, small: 1.95,
    odd: 1.95, even: 1.95,
    dragon: 1.95, dt_tie: 9.0, tiger: 1.95,
    up: 2.3, ud_tie: 4.3, down: 2.3,
    big_odd: 3.7, big_even: 3.7, small_odd: 3.7, small_even: 3.7,
    gold: 9.2, wood: 4.6, water: 2.4, fire: 4.6, earth: 9.2,
};
/** The three bet-limit tiers a member picks on the way in ("CHOOSE YOUR BET LIMIT"). */
export const KENO_DEFAULT_BET_LIMITS = [
    { min: 20, max: 4000 },
    { min: 400, max: 12000 },
    { min: 800, max: 20000 },
];
/** Big when the sum is at least this; small at or below one less. */
export const KENO_BIG_FROM = 811;
/** Numbers 1–40 count as บน, 41–80 as ล่าง. */
export const KENO_UP_MAX = 40;
/** The five elements, by sum range (inclusive). */
export const KENO_ELEMENTS = [
    { type: 'gold', from: 210, to: 695 },
    { type: 'wood', from: 696, to: 763 },
    { type: 'water', from: 764, to: 855 },
    { type: 'fire', from: 856, to: 923 },
    { type: 'earth', from: 924, to: 1410 },
];
/**
 * Read a set of 20 drawn numbers.
 *
 * Throws on anything that is not exactly 20 distinct integers in 1–80: a malformed draw
 * must never be settled, and failing loudly here is what stops it reaching the money.
 */
export function evaluateKeno(numbers) {
    if (numbers.length !== KENO_DRAWN)
        throw new Error(`KENO draw must have ${KENO_DRAWN} numbers`);
    const seen = new Set();
    for (const n of numbers) {
        if (!Number.isInteger(n) || n < 1 || n > KENO_BALLS)
            throw new Error(`KENO number out of range: ${n}`);
        if (seen.has(n))
            throw new Error(`KENO number drawn twice: ${n}`);
        seen.add(n);
    }
    const sum = numbers.reduce((s, n) => s + n, 0);
    const bigSmall = sum >= KENO_BIG_FROM ? 'big' : 'small';
    const oddEven = sum % 2 === 1 ? 'odd' : 'even';
    // "มังกร is the second digit from the right" — 998 → 9, 801 → 0.
    const dragon = Math.floor(sum / 10) % 10;
    const tiger = sum % 10;
    const dragonTiger = dragon > tiger ? 'dragon' : tiger > dragon ? 'tiger' : 'dt_tie';
    const upCount = numbers.filter((n) => n <= KENO_UP_MAX).length;
    const downCount = KENO_DRAWN - upCount;
    const upDown = upCount > downCount ? 'up' : downCount > upCount ? 'down' : 'ud_tie';
    const combo = `${bigSmall}_${oddEven}`;
    const element = KENO_ELEMENTS.find((e) => sum >= e.from && sum <= e.to).type;
    return {
        numbers: [...numbers], sum, bigSmall, oddEven, dragon, tiger, dragonTiger,
        upCount, downCount, upDown, combo, element,
    };
}
/**
 * What one bet did on one draw.
 *
 * `refunded` exists for exactly one case — มังกร or เสือ on a DT tie, which the rules
 * return in full. A บน or ล่าง bet on a 10:10 tie is `lost`: the rules do not refund it,
 * and treating the two ties alike is the mistake this function exists to prevent.
 */
export function kenoOutcome(betType, d) {
    switch (betType) {
        case 'big':
        case 'small':
            return d.bigSmall === betType ? 'won' : 'lost';
        case 'odd':
        case 'even':
            return d.oddEven === betType ? 'won' : 'lost';
        case 'dragon':
        case 'tiger':
            if (d.dragonTiger === 'dt_tie')
                return 'refunded';
            return d.dragonTiger === betType ? 'won' : 'lost';
        case 'dt_tie':
            return d.dragonTiger === 'dt_tie' ? 'won' : 'lost';
        case 'up':
        case 'down':
        case 'ud_tie':
            return d.upDown === betType ? 'won' : 'lost';
        case 'big_odd':
        case 'big_even':
        case 'small_odd':
        case 'small_even':
            return d.combo === betType ? 'won' : 'lost';
        case 'gold':
        case 'wood':
        case 'water':
        case 'fire':
        case 'earth':
            return d.element === betType ? 'won' : 'lost';
    }
}
/** Every bet type that wins on this draw — what the board highlights after the result. */
export function kenoWinningTypes(d) {
    return KENO_BET_TYPES.filter((t) => kenoOutcome(t, d) === 'won');
}
/**
 * Draw 20 distinct numbers from 1–80 with a caller-supplied source of uniform integers.
 *
 * The source is injected so the worker passes node's CSPRNG (crypto.randomInt) and tests
 * pass a deterministic one. A partial Fisher–Yates: every 20-subset, and every order of
 * it, is equally likely given a uniform source — no rejection loop, no modulo bias.
 */
export function drawKenoNumbers(randomInt) {
    const pool = Array.from({ length: KENO_BALLS }, (_, i) => i + 1);
    for (let i = 0; i < KENO_DRAWN; i++) {
        const j = i + randomInt(KENO_BALLS - i);
        [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, KENO_DRAWN);
}
export function isKenoConfig(c) {
    return !!c && typeof c === 'object' && c.engine === 'keno';
}
/**
 * The round a table is in at a given moment, on a fixed grid.
 *
 * A round runs from its slot start to its result, `durationSeconds` later; betting closes
 * `closeSeconds` before the result. The grid is anchored on the epoch plus the table's
 * offset, so every process that asks gets the same answer — the round number is the slot
 * index, which makes opening a round idempotent: two ticks asking for the same moment
 * name the same round.
 */
export function kenoSlot(nowMs, cfg, durationSeconds) {
    const period = durationSeconds * 1000;
    const offset = cfg.offsetSeconds * 1000;
    const index = Math.floor((nowMs - offset) / period);
    const openAtMs = index * period + offset;
    const resultAtMs = openAtMs + period;
    return {
        roundNumber: index - cfg.roundNumberBase,
        openAtMs,
        closeAtMs: resultAtMs - cfg.closeSeconds * 1000,
        resultAtMs,
    };
}
//# sourceMappingURL=keno.js.map